import { useState } from "react";
import { useEmployeeInfo } from "../employee/useEmployeeInfo";
import { jobService } from "@/modules/services/jobService";
import { ApplicantStatus, MoneyUnit } from "@/modules/types/enum";
import { enqueueSnackbar } from "notistack";
import { AppError } from "@/lib/utils/errors";
import { useClientTranslations } from "@/lib/translate";
import { Applicant } from "@/modules/types/conversation/conversation";

export const useApplyJob = (jobId: string) => {
  const { t } = useClientTranslations("job-apply");
  const [loading, setLoading] = useState(false);
  const [openLoginModal, setOpenLoginModal] = useState(false);
  const [openApplyJobModal, setOpenApplyJobModal] = useState(false);
  const [applyStatus, setApplyStatus] = useState<ApplicantStatus>();
  const { employeeInfo } = useEmployeeInfo();

  const applyJob = async () => {
    if (employeeInfo) {
      setOpenApplyJobModal(true);
      setOpenLoginModal(false);
    } else {
      setOpenLoginModal(true);
      setOpenApplyJobModal(false);
    }
  };

  const onApplyJob = async (
    coverLetter: string,
    minDesiredSalary: number,
    maxDesiredSalary: number,
    moneyUnit: MoneyUnit
  ) => {
    if (minDesiredSalary < 1000000) {
      enqueueSnackbar(t("minSalaryLessThan1M"), {
        variant: "error",
      });
      return;
    }

    if (maxDesiredSalary > 0 && maxDesiredSalary <= minDesiredSalary) {
      enqueueSnackbar(t("maxSalaryMustBeGreaterThanMin"), {
        variant: "error",
      });
      return;
    }

    if (!coverLetter.trim()) {
      enqueueSnackbar(t("coverLetterRequired"), {
        variant: "error",
      });
      return;
    }

    if (employeeInfo && employeeInfo.id.length > 0 && jobId.length > 0) {
      setLoading(true);
      try {
        const resp = await jobService.applyJob(
          jobId,
          employeeInfo.id,
          coverLetter,
          minDesiredSalary,
          maxDesiredSalary,
          moneyUnit
        );
        if (resp && resp.jobApplyId) {
          setLoading(false);
          enqueueSnackbar(t("appliedSuccess"), {
            variant: "success",
          });
          setApplyStatus(ApplicantStatus.pending);
          setOpenApplyJobModal(false);
        } else {
          enqueueSnackbar(t("appliedError"), {
            variant: "error",
          });
        }
      } catch (err) {
        const appErr = new AppError(err);
        appErr.setAction("useApplyJob.onApplyJob");
        appErr.setApiAction("jobService.applyJob");
        appErr.logError();
        enqueueSnackbar(appErr.message, {
          variant: "error",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const onCloseLoginModal = () => {
    setOpenLoginModal(false);
  };

  return {
    loading,
    openLoginModal,
    openApplyJobModal,
    setOpenApplyJobModal,
    applyJob,
    onCloseLoginModal,
    onApplyJob,
    applyStatus,
  };
};

export const useApplicantById = (applicantId: string) => {
  const [loading, setLoading] = useState(false);
  const [applicant, setApplicant] = useState<Applicant>();

  const fetchApplicantById = async () => {
    if (applicantId.length <= 0) return;
    setLoading(true);
    try {
      const resp = await jobService.getApplicantById(applicantId);
      if (resp) {
        setApplicant(resp.jobApply);
      }
    } catch (err) {
      const appErr = new AppError(err);
      appErr.setAction("useApplicantById.fetchApplicantById");
      appErr.setApiAction("jobService.getApplicantById");
      appErr.logError();
      enqueueSnackbar(appErr.message, { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    applicant,
    fetchApplicantById,
  };
};
